/**
 * @file Configuración — restyled to match design page-configuracion.jsx.
 *
 * 7 accordion sections:
 *  - Cuenta (account email, name, password)
 *  - Sesiones (active sessions — placeholder for now)
 *  - Lista de espera (waitlist count + list)
 *  - Ubicaciones (locations CRUD)
 *  - Servicios (consultation services CRUD)
 *  - Horario (link to /mi-horario)
 *  - Especialidades (specialties)
 *
 * All section data comes from the same hooks used by the old MyProfile
 * config tab.
 */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  User, Clock, Bell, BellRing, MapPin, Briefcase, Calendar, Award,
  ChevronRight, Plus, LogOut, Trash2, Eye, EyeOff, Pencil, Check, X,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useProfessionalLocations } from "../hooks/useProfessionalLocations";
import { useConsultationServices } from "../hooks/useConsultationServices";
import { useSchedule } from "../hooks/useSchedule";
import { useSpecialties } from "../hooks/useSpecialties";
import { useWaitlistProfessional } from "../hooks/useWaitlist";
import { useProfessionalProfile } from "../hooks/useProfessionals";
import { useNotificationPreferences, NOTIFICATION_EVENTS, CHANNEL_LABELS } from "../hooks/useNotificationPreferences";
import { toast } from "../ui";

const ICONS = { User, Clock, Bell, BellRing, MapPin, Briefcase, Calendar, Award };

const DAY_NAMES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const initials = (name) => (name || "").split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";

const formatPasswordUpdatedAt = (iso) => {
  if (!iso) return "Aún no has cambiado tu contraseña";
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "Última actualización desconocida";
  const diffMs = Date.now() - then.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays < 1)   return "Última actualización hoy";
  if (diffDays === 1) return "Última actualización ayer";
  if (diffDays < 30)  return `Última actualización hace ${diffDays} días`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `Última actualización hace ${diffMonths} ${diffMonths === 1 ? "mes" : "meses"}`;
  const diffYears = Math.floor(diffMonths / 12);
  return `Última actualización hace ${diffYears} ${diffYears === 1 ? "año" : "años"}`;
};

export default function Configuracion() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [openId, setOpenId] = useState("cuenta");

  const { locations } = useProfessionalLocations();
  const { services, addService, updateService, removeService } = useConsultationServices();
  const { schedules } = useSchedule();
  const { specialties, addSpecialty, updateSpecialty, removeSpecialty: removeSpecialtyHook } = useSpecialties();
  const waitlist = useWaitlistProfessional(true);
  const { profile, updateProfile } = useProfessionalProfile();

  const sections = [
    { id: "cuenta", ic: "User", ttl: "Cuenta", sub: "Email, contraseña, datos personales" },
    { id: "notif", ic: "BellRing", ttl: "Notificaciones", sub: "Qué avisos recibes y por qué canal" },
    { id: "sesiones", ic: "Clock", ttl: "Sesiones activas", sub: "Dispositivo actual" },
    {
      id: "espera",
      ic: "Bell",
      ttl: "Lista de espera",
      sub: "Pacientes que esperan disponibilidad",
      meta: waitlist.count > 0 ? `${waitlist.count} ${waitlist.count === 1 ? "paciente" : "pacientes"}` : null,
    },
    {
      id: "ubic",
      ic: "MapPin",
      ttl: "Mis ubicaciones",
      sub: "Consultorios y direcciones visibles al paciente",
      meta: locations.length > 0 ? String(locations.length) : null,
    },
    {
      id: "serv",
      ic: "Briefcase",
      ttl: "Mis servicios",
      sub: "Tipos de consulta, duración y precio",
      meta: services.length > 0 ? String(services.length) : null,
    },
    { id: "horario", ic: "Calendar", ttl: "Mi horario", sub: "Disponibilidad y bloqueos por día" },
    {
      id: "esp",
      ic: "Award",
      ttl: "Especialidades",
      sub: "Especialidades y certificaciones",
      meta: specialties.length > 0 ? String(specialties.length) : null,
    },
  ];

  return (
    <>
      <div className="pg-head">
          <div>
            <div className="cd-eyebrow">Ajustes de la cuenta</div>
            <h1 className="pg-title"><em>Configuración</em></h1>
            <p className="pg-sub">Gestiona tu cuenta, ubicaciones, horarios, servicios y más.</p>
          </div>
        </div>

        <div className="cfg-list">
          {sections.map((s) => {
            const Ic = ICONS[s.ic];
            const open = openId === s.id;
            return (
              <div key={s.id} className={"cfg-row" + (open ? " open" : "")}>
                <div
                  className="cfg-head"
                  onClick={() => setOpenId(open ? null : s.id)}
                >
                  <div className="ic"><Ic size={16} /></div>
                  <div className="info">
                    <div className="ttl">{s.ttl}</div>
                    <div className="sub">{s.sub}</div>
                  </div>
                  {s.meta && <span className="meta">{s.meta}</span>}
                  <span className="chev"><ChevronRight size={16} /></span>
                </div>
                {open && (
                  <CfgBody
                    id={s.id}
                    user={user}
                    locations={locations}
                    services={services}
                    addService={addService}
                    updateService={updateService}
                    removeService={removeService}
                    schedules={schedules}
                    specialties={specialties}
                    addSpecialty={addSpecialty}
                    updateSpecialty={updateSpecialty}
                    removeSpecialty={removeSpecialtyHook}
                    waitlist={waitlist}
                    navigate={navigate}
                    profile={profile}
                    updateProfile={updateProfile}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div
          style={{
            marginTop: 28, padding: 20, borderRadius: "var(--radius)",
            border: "1px solid var(--line)", background: "var(--surface)",
            display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
          }}
        >
          <div
            style={{
              width: 36, height: 36, borderRadius: 9,
              background: "oklch(0.96 0.04 25)", color: "oklch(0.5 0.18 25)",
              display: "grid", placeItems: "center",
            }}
          >
            <LogOut size={16} />
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: "var(--ink)" }}>Cerrar sesión</div>
            <div style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
              Sale de tu cuenta en este dispositivo.
            </div>
          </div>
          <button
            type="button"
            className="pg-btn"
            style={{ color: "oklch(0.5 0.18 25)", borderColor: "oklch(0.85 0.06 25)" }}
            onClick={async () => { await logout(); navigate("/login"); }}
          >
            Cerrar sesión
          </button>
        </div>
    </>
  );
}

function CfgBody({ id, user, locations, services, addService, updateService, removeService, schedules, specialties, addSpecialty, updateSpecialty, removeSpecialty, waitlist, navigate, profile, updateProfile }) {
  switch (id) {
    case "cuenta": return <CuentaBody user={user} />;
    case "notif": return <NotificacionesBody />;
    case "sesiones": return <SesionesBody />;
    case "espera": return <EsperaBody waitlist={waitlist} />;
    case "ubic": return <UbicBody locations={locations} navigate={navigate} />;
    case "serv": return <ServBody services={services} addService={addService} updateService={updateService} removeService={removeService} profile={profile} updateProfile={updateProfile} />;
    case "horario": return <HorarioBody schedules={schedules} navigate={navigate} profile={profile} updateProfile={updateProfile} />;
    case "esp": return <EspBody specialties={specialties} addSpecialty={addSpecialty} updateSpecialty={updateSpecialty} removeSpecialty={removeSpecialty} />;
    default: return null;
  }
}

/**
 * Visibility toggle row — mirrors the onboarding .ob-loc-tele pattern.
 * Optimistic: flips local state immediately, PUTs to /professional/me,
 * rolls back + toasts on failure.
 */
function VisibilityToggle({ field, value, profile, updateProfile, title, sub }) {
  const [optimistic, setOptimistic] = useState(value);
  React.useEffect(() => { setOptimistic(value); }, [value]);
  const flip = async () => {
    const next = !optimistic;
    setOptimistic(next);
    try {
      await updateProfile({ [field]: next });
    } catch {
      setOptimistic(!next);
      toast.error("No se pudo guardar");
    }
  };
  return (
    <div className="ob-loc-tele" style={{ marginBottom: 14 }}>
      <div className="icon">{optimistic ? <Eye size={18} /> : <EyeOff size={18} />}</div>
      <div className="copy">
        <div className="ttl">{title}</div>
        <div className="sub">{sub}</div>
      </div>
      <button
        type="button"
        className={"ob-toggle" + (optimistic ? " on" : "")}
        aria-pressed={optimistic}
        onClick={flip}
      />
    </div>
  );
}

function CuentaBody({ user }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [pwOpen, setPwOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [wrongCurrent, setWrongCurrent] = useState(false);
  const [visibleField, setVisibleField] = useState(null);

  const isOAuth = user?.authProvider && user.authProvider !== "EMAIL";
  const providerName = { GOOGLE: "Google", APPLE: "Apple" }[user?.authProvider] || user?.authProvider;

  const canSave = currentPassword && newPassword && confirmPassword
    && newPassword === confirmPassword && newPassword.length >= 10;

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError("");
    setWrongCurrent(false);
    if (newPassword.length < 10) {
      setError("La nueva contraseña debe tener al menos 10 caracteres.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Las contraseñas no coinciden.");
      return;
    }
    setSubmitting(true);
    try {
      const { authService } = await import("../services/auth");
      await authService.changePassword(currentPassword, newPassword);
      toast("Contraseña cambiada correctamente. Inicia sesión de nuevo.");
      await logout();
      navigate("/login");
    } catch (err) {
      setError(err.response?.data?.error || "Error al cambiar la contraseña.");
      setWrongCurrent(err.response?.status === 401);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setWrongCurrent(false);
    setVisibleField(null);
    setPwOpen(false);
  };

  return (
    <div className="cfg-body">
      <div className="form-row">
        <div className="form-field">
          <label>Email de la cuenta</label>
          <input className="form-input" defaultValue={user?.email || ""} disabled />
          <div className="help">Para cambiar tu email contacta a soporte.</div>
        </div>
        <div className="form-field">
          <label>Nombre completo</label>
          <input
            className="form-input"
            defaultValue={[user?.firstName, user?.lastName].filter(Boolean).join(" ")}
            disabled
          />
          <div className="help">Edita tu nombre desde "Mi perfil público".</div>
        </div>
      </div>

      <div className="cfg-pw-card">
        <div className="cfg-pw-head" onClick={() => setPwOpen(!pwOpen)}>
          <div className="cfg-pw-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: "13.5px", color: "var(--ink)" }}>Contraseña</div>
            <div style={{ fontSize: 12, color: "var(--ink-3)" }}>{formatPasswordUpdatedAt(user?.passwordUpdatedAt)}</div>
          </div>
          <ChevronRight size={16} className="cfg-pw-chev" style={pwOpen ? { transform: "rotate(90deg)" } : undefined} />
        </div>
        {pwOpen && (
          <div className="cfg-pw-body">
            {isOAuth ? (
              <p style={{ fontSize: 13.5, color: "var(--ink-3)", margin: 0 }}>
                Iniciaste sesión con {providerName}. La contraseña se administra desde tu cuenta de {providerName}.
              </p>
            ) : (
              <form onSubmit={handleChangePassword}>
                <div className="form-field" style={{ marginBottom: 14 }}>
                  <label htmlFor="pw-current">Contraseña actual</label>
                  <div className="pw-field-wrap">
                    <input
                      id="pw-current"
                      className="form-input"
                      type={visibleField === "current" ? "text" : "password"}
                      placeholder="Ingresa tu contraseña actual"
                      style={{ paddingRight: 42 }}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className={"pw-eye-btn" + (visibleField === "current" ? " active" : "")}
                      onClick={() => setVisibleField(visibleField === "current" ? null : "current")}
                    >
                      {visibleField === "current" ? <EyeOff size={17} /> : <Eye size={17} />}
                    </button>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="pw-new">Nueva contraseña</label>
                    <div className="pw-field-wrap">
                      <input
                        id="pw-new"
                        className="form-input"
                        type={visibleField === "new" ? "text" : "password"}
                        placeholder="Mínimo 10 caracteres"
                        style={{ paddingRight: 42 }}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className={"pw-eye-btn" + (visibleField === "new" ? " active" : "")}
                        onClick={() => setVisibleField(visibleField === "new" ? null : "new")}
                      >
                        {visibleField === "new" ? <EyeOff size={17} /> : <Eye size={17} />}
                      </button>
                    </div>
                  </div>
                  <div className="form-field">
                    <label htmlFor="pw-confirm">Confirmar</label>
                    <div className="pw-field-wrap">
                      <input
                        id="pw-confirm"
                        className="form-input"
                        type={visibleField === "confirm" ? "text" : "password"}
                        placeholder="Repite tu nueva contraseña"
                        style={{ paddingRight: 42 }}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className={"pw-eye-btn" + (visibleField === "confirm" ? " active" : "")}
                        onClick={() => setVisibleField(visibleField === "confirm" ? null : "confirm")}
                      >
                        {visibleField === "confirm" ? <EyeOff size={17} /> : <Eye size={17} />}
                      </button>
                    </div>
                  </div>
                </div>
                {error && (
                  <div style={{ marginTop: 4 }}>
                    <div style={{ color: "oklch(0.48 0.16 25)", fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                      {error}
                    </div>
                    {wrongCurrent && (
                      <Link
                        to="/forgot-password"
                        style={{ fontSize: 12, marginLeft: 16, marginTop: 2, display: "inline-block", color: "var(--primary-deep)", textDecoration: "underline" }}
                      >
                        ¿Olvidaste tu contraseña? Restablécela aquí.
                      </Link>
                    )}
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18, paddingTop: 16, flexWrap: "wrap", borderTop: "1px solid var(--line)" }}>
                  <button type="button" className="pg-btn" onClick={handleCancel}>
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="pg-btn solid"
                    disabled={!canSave || submitting}
                    style={!canSave ? { opacity: 0.45, cursor: "not-allowed" } : {}}
                  >
                    {submitting ? "Cambiando…" : "Cambiar contraseña"}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Notification matrix — for each event, a Push and/or Email toggle controlling
 * which of the professional's OWN notifications they receive. Toggles are
 * optimistic (handled by the hook): flips immediately, PUTs the full matrix,
 * rolls back + toasts on failure. Patient notifications are unaffected.
 */
function NotificacionesBody() {
  const { prefs, isLoading, toggle } = useNotificationPreferences();
  return (
    <div className="cfg-body">
      <p style={{ fontSize: 13, color: "var(--ink-3)", margin: "0 0 14px" }}>
        Elige qué avisos recibes y por qué canal. Esto no afecta los recordatorios
        que reciben tus pacientes.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {NOTIFICATION_EVENTS.map((ev) => (
          <div
            key={ev.key}
            style={{
              display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
              padding: "12px 14px", background: "var(--surface)",
              border: "1px solid var(--line)", borderRadius: 9,
            }}
          >
            <div style={{ flex: 1, minWidth: 140, fontWeight: 600, fontSize: 13.5, color: "var(--ink)" }}>
              {ev.label}
            </div>
            <div style={{ display: "flex", gap: 18 }}>
              {ev.channels.map((ch) => {
                const on = prefs[ev.key]?.[ch] !== false;
                return (
                  <div key={ch} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 11.5, color: "var(--ink-3)" }}>{CHANNEL_LABELS[ch]}</span>
                    <button
                      type="button"
                      className={"ob-toggle" + (on ? " on" : "")}
                      aria-pressed={on}
                      aria-label={`${CHANNEL_LABELS[ch]} — ${ev.label}`}
                      disabled={isLoading}
                      onClick={() => toggle(ev.key, ch)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SesionesBody() {
  return (
    <div className="cfg-body">
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div
          style={{
            display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
            padding: "12px 14px", background: "var(--surface)",
            border: "1px solid var(--line)", borderRadius: 9,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {navigator.userAgent.includes("Mac") ? "Mac" : navigator.userAgent.includes("iPhone") ? "iPhone" : "Este navegador"}
            </div>
            <div style={{ fontSize: 12, color: "var(--ink-3)" }}>Activa ahora</div>
          </div>
          <span
            style={{
              fontSize: 11, fontFamily: "var(--mono)", fontWeight: 600,
              padding: "3px 9px", borderRadius: 999, flexShrink: 0,
              background: "var(--accent-soft)", color: "var(--accent-deep)",
              letterSpacing: "0.06em", textTransform: "uppercase",
            }}
          >
            Actual
          </span>
        </div>
      </div>
      <div className="help" style={{ marginTop: 12 }}>
        El listado completo de dispositivos estará disponible próximamente.
      </div>
    </div>
  );
}

function EsperaBody({ waitlist }) {
  if (!waitlist.count) {
    return (
      <div className="cfg-body">
        <p style={{ fontSize: 13, color: "var(--ink-3)", margin: 0 }}>
          No hay pacientes en lista de espera. Cuando un paciente intenta agendar y no encuentra
          horarios disponibles, se le ofrece unirse a tu lista.
        </p>
      </div>
    );
  }
  return (
    <div className="cfg-body">
      <p style={{ fontSize: 13, color: "var(--ink-3)", margin: "0 0 14px" }}>
        Pacientes que esperan disponibilidad. Si se cancela una cita, se les notifica por orden de llegada.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {(waitlist.entries || []).map((p, i) => {
          const name = `${p.firstName || ""} ${p.lastName || ""}`.trim() || "Paciente";
          return (
            <div key={p.id || i} className="cfg-espera-row">
              <div className={"cd-avatar a" + ((i % 6) + 1)}>{initials(name)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: "var(--ink)" }}>{name}</div>
                <div style={{ fontSize: 12, color: "var(--ink-3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p.note || "Cualquier disponibilidad"}
                </div>
              </div>
              <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--ink-3)", flexShrink: 0 }}>
                {p.createdAt ? new Date(p.createdAt).toLocaleDateString("es-MX", { day: "numeric", month: "short" }) : ""}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UbicBody({ locations, navigate }) {
  return (
    <div className="cfg-body">
      {locations.length === 0 ? (
        <p style={{ fontSize: 13, color: "var(--ink-3)", margin: "0 0 14px" }}>
          Aún no tienes ubicaciones. Agrega un consultorio para que los pacientes puedan agendar.
        </p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {locations.map((u, i) => (
            <div
              key={u.id || i}
              style={{
                display: "flex", alignItems: "center", gap: 14,
                padding: "14px", background: "var(--surface)",
                border: "1px solid var(--line)", borderRadius: 9,
              }}
            >
              <div
                style={{
                  width: 36, height: 36, borderRadius: 9,
                  background: "var(--accent-soft)", color: "var(--accent-deep)",
                  display: "grid", placeItems: "center",
                }}
              >
                <MapPin size={16} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5, color: "var(--ink)" }}>{u.name || "Ubicación"}</div>
                  {u.isPrimary && (
                    <span
                      style={{
                        fontSize: 10, fontFamily: "var(--mono)", fontWeight: 600,
                        padding: "2px 8px", borderRadius: 999,
                        background: "var(--primary-50)", color: "var(--primary-deep)",
                        letterSpacing: "0.06em", textTransform: "uppercase",
                      }}
                    >
                      Principal
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
                  {u.isVirtual ? "Telemedicina · videoconsulta" : (u.address || "")}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        className="pg-btn solid"
        style={{ marginTop: 12 }}
        onClick={() => navigate("/mi-perfil/ubicacion")}
      >
        <Plus size={14} /> Agregar ubicación
      </button>
    </div>
  );
}

function ServBody({ services, addService, updateService, removeService, profile, updateProfile }) {
  const [svcName, setSvcName] = useState("");
  const [svcPrice, setSvcPrice] = useState("");
  const [svcDescription, setSvcDescription] = useState("");
  const [adding, setAdding] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const startEdit = (s) => {
    setEditingId(s.id);
    setEditName(s.name || "");
    setEditPrice(s.price != null ? String(s.price) : "");
    setEditDescription(s.description || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName(""); setEditPrice(""); setEditDescription("");
  };

  const saveEdit = async () => {
    if (!editName.trim()) return toast.error("Nombre del servicio requerido");
    const priceNum = Number(editPrice);
    if (!editPrice || !(priceNum > 0)) return toast.error("Precio debe ser mayor a 0");
    setSavingEdit(true);
    try {
      await updateService(editingId, {
        name: editName.trim(),
        price: priceNum,
        description: editDescription.trim() || null,
      });
      cancelEdit();
    } catch (e) {
      toast.error(e?.response?.data?.message || "No se pudo guardar");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAdd = async () => {
    if (!svcName.trim()) return toast.error("Nombre del servicio requerido");
    const priceNum = Number(svcPrice);
    if (!svcPrice || !(priceNum > 0)) return toast.error("Precio debe ser mayor a 0");
    setAdding(true);
    try {
      await addService({
        name: svcName.trim(),
        price: priceNum,
        description: svcDescription.trim() || undefined,
      });
      setSvcName(""); setSvcPrice(""); setSvcDescription("");
    } catch (e) {
      toast.error(e?.response?.data?.message || "No se pudo agregar");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (id, name) => {
    if (!window.confirm(`¿Eliminar el servicio "${name}"?`)) return;
    try {
      await removeService(id);
    } catch (e) {
      toast.error(e?.response?.data?.message || "No se pudo eliminar");
    }
  };

  return (
    <div className="cfg-body">
      <VisibilityToggle
        field="showPrices"
        value={profile?.showPrices ?? true}
        updateProfile={updateProfile}
        title="Mostrar precios de consultas"
        sub="Si tus precios varían, puedes ocultarlos. El paciente verá el monto al confirmar la cita."
      />
      {services.length === 0 ? (
        <p style={{ fontSize: 13, color: "var(--ink-3)", margin: "0 0 14px" }}>
          Define tipos de consulta con su precio para que los pacientes puedan elegir al agendar.
        </p>
      ) : (
        <div className="pg-table-card" style={{ background: "var(--surface)" }}>
          <div className="pg-table-head" style={{ gridTemplateColumns: "2fr 100px 100px 40px" }}>
            <div>Servicio</div>
            <div>Duración</div>
            <div>Precio</div>
            <div />
          </div>
          {services.map((s, i) => {
            const isEditing = editingId === s.id;
            if (isEditing) {
              return (
                <div key={s.id || i} className="cfg-svc-edit">
                  <div className="cfg-svc-edit-row">
                    <input
                      type="text"
                      placeholder="Nombre del servicio"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}
                      maxLength={200}
                      autoFocus
                    />
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="Precio"
                      value={editPrice}
                      onChange={(e) => setEditPrice(e.target.value.replace(/[^0-9.]/g, ""))}
                      onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}
                    />
                    <div className="cfg-svc-edit-actions">
                      <button type="button" className="cfg-row-cancel" onClick={cancelEdit} aria-label="Cancelar">
                        <X size={14} />
                      </button>
                      <button type="button" className="cfg-row-save" onClick={saveEdit} disabled={savingEdit} aria-label="Guardar">
                        <Check size={14} />
                      </button>
                    </div>
                  </div>
                  <textarea
                    className="cfg-svc-desc"
                    placeholder="Descripción (opcional, máx 300 caracteres)"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value.slice(0, 300))}
                    rows={2}
                  />
                </div>
              );
            }
            return (
              <div
                key={s.id || i}
                className="pg-table-row"
                style={{ gridTemplateColumns: "2fr 100px 100px 70px", cursor: "default", alignItems: "center" }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: "var(--ink)", fontSize: 13.5 }}>{s.name}</div>
                  {s.description && (
                    <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2, lineHeight: 1.4 }}>
                      {s.description}
                    </div>
                  )}
                </div>
                <div style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--ink-2)" }}>
                  {s.durationMinutes || s.duration || profile?.sessionDuration || "—"} min
                </div>
                <div style={{ fontFamily: "var(--mono)", fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>
                  {s.price ? `$${Number(s.price).toLocaleString("es-MX")}` : "—"}
                </div>
                <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    onClick={() => startEdit(s)}
                    aria-label={`Editar ${s.name}`}
                    className="cfg-row-edit"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemove(s.id, s.name)}
                    aria-label={`Eliminar ${s.name}`}
                    className="cfg-row-del"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="cfg-svc-add">
        <input
          type="text"
          placeholder="Nombre del servicio"
          value={svcName}
          onChange={(e) => setSvcName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
          maxLength={200}
        />
        <input
          type="text"
          inputMode="decimal"
          placeholder="Precio"
          value={svcPrice}
          onChange={(e) => setSvcPrice(e.target.value.replace(/[^0-9.]/g, ""))}
          onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
        />
        <button type="button" className="pg-btn solid" onClick={handleAdd} disabled={adding}>
          <Plus size={14} /> {adding ? "Agregando…" : "Agregar"}
        </button>
      </div>
      <textarea
        className="cfg-svc-desc"
        placeholder="Descripción (opcional, máx 300 caracteres) — se muestra junto al precio en tu perfil público"
        value={svcDescription}
        onChange={(e) => setSvcDescription(e.target.value.slice(0, 300))}
        rows={2}
      />
      <div className="help" style={{ marginTop: 8 }}>
        La duración por defecto se aplica a todos los servicios (configurable en Mi Perfil).
      </div>
    </div>
  );
}

function HorarioBody({ schedules, navigate, profile, updateProfile }) {
  const byDay = {};
  (schedules || []).forEach((s) => {
    if (!s.isActive) return;
    (byDay[s.dayOfWeek] ||= []).push(`${s.startTime} – ${s.endTime}`);
  });
  return (
    <div className="cfg-body">
      <VisibilityToggle
        field="showSchedule"
        value={profile?.showSchedule ?? true}
        updateProfile={updateProfile}
        title="Mostrar mi horario semanal"
        sub="Si lo ocultas, el paciente elige fecha y hora disponible directamente, sin ver tu agenda completa."
      />
      <div className="cfg-horario-grid">
        {[1, 2, 3, 4, 5, 6, 0].map((d) => (
          <div key={d} className={"cfg-horario-card" + (byDay[d] ? "" : " closed")}>
            <div className="day">{DAY_NAMES[d]}</div>
            <div className="hours">{byDay[d] ? byDay[d].join(" · ") : "Cerrado"}</div>
          </div>
        ))}
      </div>
      <button
        type="button"
        className="pg-btn solid"
        style={{ marginTop: 12 }}
        onClick={() => navigate("/mi-horario")}
      >
        Editar horario
      </button>
    </div>
  );
}

function EspBody({ specialties, addSpecialty, updateSpecialty, removeSpecialty }) {
  const [newName, setNewName] = useState("");
  const [newCedula, setNewCedula] = useState("");
  const [adding, setAdding] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCedula, setEditCedula] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const startEdit = (it) => {
    setEditingId(it.id);
    setEditName(it.label || it.name || it.specialty || "");
    setEditCedula(it.cedula || "");
  };

  const cancelEdit = () => {
    setEditingId(null); setEditName(""); setEditCedula("");
  };

  const saveEdit = async () => {
    if (!editName.trim()) return toast.error("Nombre requerido");
    if (!editCedula.trim()) return toast.error("Cédula requerida");
    setSavingEdit(true);
    try {
      await updateSpecialty(editingId, {
        name: editName.trim(),
        cedula: editCedula.trim(),
      });
      cancelEdit();
    } catch (e) {
      toast.error(e?.response?.data?.message || "No se pudo guardar");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAdd = async () => {
    if (!newName.trim()) return toast.error("Nombre requerido");
    if (!newCedula.trim()) return toast.error("Cédula requerida");
    setAdding(true);
    try {
      await addSpecialty({ name: newName.trim(), cedula: newCedula.trim() });
      setNewName(""); setNewCedula("");
    } catch (e) {
      toast.error(e?.response?.data?.message || "No se pudo agregar");
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (id, label) => {
    if (!window.confirm(`¿Eliminar la especialidad "${label}"?`)) return;
    try {
      await removeSpecialty(id);
    } catch (e) {
      toast.error(e?.response?.data?.message || "No se pudo eliminar");
    }
  };

  return (
    <div className="cfg-body">
      {specialties && specialties.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {specialties.map((it, i) => {
            const isEditing = editingId === it.id;
            if (isEditing) {
              return (
                <div key={it.id || i} className="cfg-esp-edit">
                  <input
                    type="text"
                    placeholder="Especialidad"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}
                    maxLength={200}
                    autoFocus
                  />
                  <input
                    type="text"
                    placeholder="Cédula"
                    value={editCedula}
                    onChange={(e) => setEditCedula(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}
                    maxLength={50}
                  />
                  <div className="cfg-svc-edit-actions">
                    <button type="button" className="cfg-row-cancel" onClick={cancelEdit} aria-label="Cancelar">
                      <X size={14} />
                    </button>
                    <button type="button" className="cfg-row-save" onClick={saveEdit} disabled={savingEdit} aria-label="Guardar">
                      <Check size={14} />
                    </button>
                  </div>
                </div>
              );
            }
            const label = it.label || it.name || it.specialty;
            return (
              <div
                key={it.id || i}
                style={{
                  display: "flex", alignItems: "center", gap: 14,
                  padding: 14, background: "var(--surface)",
                  border: "1px solid var(--line)", borderRadius: 9,
                }}
              >
                <div
                  style={{
                    width: 36, height: 36, borderRadius: 9,
                    background: "var(--accent-soft)", color: "var(--accent-deep)",
                    display: "grid", placeItems: "center", flexShrink: 0,
                  }}
                >
                  <Award size={16} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5, color: "var(--ink)" }}>{label}</div>
                  {it.cedula && (
                    <div style={{ fontSize: 12.5, color: "var(--ink-3)", fontFamily: "var(--mono)" }}>
                      Cédula {it.cedula}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  <button
                    type="button"
                    onClick={() => startEdit(it)}
                    aria-label={`Editar ${label}`}
                    className="cfg-row-edit"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemove(it.id, label)}
                    aria-label={`Eliminar ${label}`}
                    className="cfg-row-del"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p style={{ fontSize: 13, color: "var(--ink-3)", margin: "0 0 14px" }}>
          Agrega tus especialidades y cédulas profesionales para mostrarlas en tu perfil público.
        </p>
      )}

      <div className="cfg-esp-add">
        <input
          type="text"
          placeholder="Especialidad (ej. Pediatría)"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
          maxLength={200}
        />
        <input
          type="text"
          placeholder="Cédula profesional"
          value={newCedula}
          onChange={(e) => setNewCedula(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); }}
          maxLength={50}
        />
        <button type="button" className="pg-btn solid" onClick={handleAdd} disabled={adding}>
          <Plus size={14} /> {adding ? "Agregando…" : "Agregar"}
        </button>
      </div>
    </div>
  );
}
